import i18n from 'i18next';

import {apiClient} from 'core/apiClient';
import {CODIFIERS_LINK} from 'core/constants/codifiers';
import {RestError} from 'core/errors';
import {ApiFilters, ApiPaging, ApiSortings, SimplePaginationContent} from 'types/api';
import {
    BatchRecallParams,
    BrandCreateParams,
    DeclarationResponsibility,
    DropoutBatchResponse,
    GtinsGridRawRow,
    GtinsModeFilter,
    GtinsViewMode,
    GtinUploadDataItem,
    PackagingFormatType,
} from 'types/gtins';

const GTINS_URL = '/service/product-cards';
const GTIN_UPLOAD_URL = '/service/product-cards/load';
const GTINS_MODE_FILTER_KEY = 'modeFilter';
const GTINS_ROUTE = {
    MY: '/registries/my',
    MY_CREATED: '/registries/my-created',
    ALL: '/registries/all',
    FAVORITES: '/registries/favorites',
} as const;

const gtinsViewModeByPath: Record<string, GtinsViewMode> = {
    [GTINS_ROUTE.MY]: 'MY',
    [GTINS_ROUTE.MY_CREATED]: 'MY_CREATED',
    [GTINS_ROUTE.ALL]: 'ALL',
    [GTINS_ROUTE.FAVORITES]: 'MY',
};

const getResponseErrors = (data: any) => {
    if (Array.isArray(data?.errors)) return data.errors;
    if (Array.isArray(data?.fullErrorsList)) return data.fullErrorsList;
    if (Array.isArray(data)) return data.flatMap((item) => item?.errors ?? []);

    return [];
};

const throwResponseErrors = (errors: any[]) => {
    if (!errors.length) return;

    const firstError = errors[0];
    const error = new Error(
        firstError?.message || firstError?.error || i18n.t('errors.occurred'),
    ) as RestError;
    error.name = 'RestError';
    error.fullErrorsList = errors;

    throw error;
};

export async function fetchGtinsGrid(params: {
    paging: ApiPaging;
    filters: ApiFilters;
    sortings: ApiSortings;
    gtinOrProduct?: string;
}): Promise<SimplePaginationContent<GtinsGridRawRow>> {
    const viewMode = gtinsViewModeByPath[location.pathname] ?? 'MY';
    const modeFilter = params.filters.find((filter) => filter.id === GTINS_MODE_FILTER_KEY)
        ?.value?.[0] as GtinsModeFilter | undefined;
    const filters = params.filters.filter((filter) => filter.id !== GTINS_MODE_FILTER_KEY);

    const result = (
        await apiClient.post<GtinsGridRawRow[]>(GTINS_URL, {
            ...params,
            filters,
            viewMode,
            modeFilter,
        })
    ).data;

    const hasNext = result.length > params.paging.pageSize;

    return {
        content: hasNext ? result.slice(0, -1) : result,
        hasNext,
    };
}

export async function uploadGtin(data: Array<GtinUploadDataItem>) {
    const responseData = (await apiClient.post(GTIN_UPLOAD_URL, {products: data})).data;

    throwResponseErrors(getResponseErrors(responseData));

    return responseData;
}

export async function editGtin(gtin: string | number) {
    return (await apiClient.get<{fields: Record<string, any>}>(`service/product-cards/${gtin}`))
        .data;
}

export async function editGtinVersion(productId: string) {
    return (
        await apiClient.get<{fields: Record<string, any>}>(
            `service/product-cards/version/${productId}`,
        )
    ).data;
}

export async function recallGtin({
    productCode,
    reason,
    batchNumber,
    regulatorDocumentNumber,
    recallDecisionDate,
    comment,
}: {
    productCode: string;
    reason: string;
    batchNumber?: string;
    regulatorDocumentNumber?: string;
    recallDecisionDate?: string;
    comment?: string;
}) {
    return (
        await apiClient.post(`marking/dropout/gtin`, undefined, {
            baseURL: '/api/v1',
            params: {
                productCode,
                reason,
                ...(batchNumber && {batchNumber}),
                ...(regulatorDocumentNumber && {
                    supportingDocumentNumber: regulatorDocumentNumber,
                }),
                ...(recallDecisionDate && {supportingDocumentDate: recallDecisionDate}),
                ...(comment && {comment}),
            },
        })
    ).data;
}

export async function fetchDropoutBatches(params: {
    productCode: string;
    batchNumber: string;
}): Promise<DropoutBatchResponse[]> {
    return (
        await apiClient.get<DropoutBatchResponse[]>('/marking/dropout/batches', {
            params,
        })
    ).data;
}

export async function recallBatch({
    regulatorDocumentNumber,
    recallDecisionDate,
    ...params
}: BatchRecallParams) {
    return (
        await apiClient.post('/marking/dropout/gtin', undefined, {
            params: {
                ...params,
                ...(regulatorDocumentNumber && {
                    supportingDocumentNumber: regulatorDocumentNumber,
                }),
                ...(recallDecisionDate && {supportingDocumentDate: recallDecisionDate}),
            },
        })
    ).data;
}

export async function deleteGtin(productId: string) {
    return (await apiClient.delete(`service/product-cards/${productId}`)).data;
}

export async function getHsnCodes(codes: string[]): Promise<{code: string; description: string}[]> {
    return (await apiClient.post(`${CODIFIERS_LINK}/api/v1/hsn/product/search`, codes)).data;
}

export async function getBrandNames(name: string) {
    try {
        return (
            await apiClient.post(`/service/product-cards/brand-owners`, {
                paging: {pageNumber: 0, pageSize: 40},
                name,
            })
        ).data.map((d: any) => ({...d, value: d.brandName}));
    } catch (e) {
        console.error(e);
        return [];
    }
}

export async function getPackagingFormatType(): Promise<PackagingFormatType[]> {
    return (await apiClient.get('/service/dict/packaging-format-type')).data;
}

export async function setFavorite({gtin, status}: {gtin: string; status: boolean}) {
    const url = `/service/product-cards/${gtin}/favorite`;
    const response = status ? await apiClient.post(url) : await apiClient.delete(url);
    return response.data;
}

export async function setFavorites({gtins, status}: {gtins: string[]; status: boolean}) {
    const url = '/service/product-cards/favorites';
    const response = status
        ? await apiClient.post(url, gtins)
        : await apiClient.delete(url, {data: gtins});

    return response.data;
}

export async function setBlock({productId, status}: {productId: string; status: boolean}) {
    const url = `/service/product-cards/${productId}/${status ? 'block' : 'unblock'}`;
    return (await apiClient.post<{fields: Record<string, any>}>(url)).data;
}

export async function setSubmit({
    productId,
    status,
    reason,
}: {
    productId: string;
    status: boolean;
    reason?: string;
}) {
    const url = `/service/product-cards/${productId}/${status ? 'submit' : 'reject'}`;
    return (
        await apiClient.post<{fields: Record<string, any>}>(url, null, {
            ...(reason && {params: {reason}}),
        })
    ).data;
}

export async function setRecall({productId}: {productId: string}) {
    const url = `/service/product-cards/${productId}/recall`;
    return (await apiClient.post<{fields: Record<string, any>}>(url)).data;
}

export async function setApprove({productId}: {productId: string}) {
    const url = `/service/product-cards/${productId}/approve`;
    return (await apiClient.post<{fields: Record<string, any>}>(url)).data;
}

export async function setPublish({productId}: {productId: string}) {
    const url = `/service/product-cards/${productId}/publish`;
    return (await apiClient.post<{fields: Record<string, any>}>(url)).data;
}

export async function releaseNewVersion({productId}: {productId: string}) {
    const url = `/service/product-cards/${productId}/new-version`;
    return (await apiClient.post<{fields: Record<string, any>}>(url)).data;
}

export async function getDeclarationResponsibility() {
    const url = `/service/product-cards/declaration/text`;
    return (await apiClient.get<DeclarationResponsibility>(url)).data;
}

export async function createBrand(payload: BrandCreateParams) {
    const url = '/service/product-cards/brand-owners/create';
    return (await apiClient.post(url, payload)).data;
}
